import { consume } from '@lit/context';
import { LitElement, css, html, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';

import { CHAT_INPUT_MAX_CHARS } from '../../../../game/Chat';
import type { BindName } from '../../../../game/types/Input';
import { EMOTE_NAMES } from '../../../../generated/emotes';
import type { FlagCode } from '../../../../generated/flags';
import { findFlagByPrefix, resolveFlag } from '../../../../services/Flags';
import { escapePlayerName } from '../../../../services/PlayerNames';
import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { scrollbarThinHover } from '../../../theme/scrollbar.css';
import { tokens } from '../../../theme/tokens.css';
import {
  uiState as uiStateProxy,
  type ChatMessageEntry,
  type ChatMessagePlayer,
  type ChatMessageType,
} from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { flagStyle, renderChatText } from '../chat-inline';
import { captureReadingAnchor, jumpsToLatest, restoreReadingAnchor, type ReadingAnchor } from '../chat-scroll';
import './radio-panel';

/**
 * Room kept above the chat box when capping its height, so the input and the
 * command hint stay below the top bar however tall the chat was resized (a
 * height saved on a bigger monitor, or a drag to the top of the screen).
 * chat-area's 14px bottom, the input and its margin (40px), the command hint
 * wrapped to two lines at the 330px minimum width (42px), and the top bar's
 * 58px (touch chips) plus an 11px gap. Feeds the CSS cap and the drag clamp,
 * so a saved height is always one that was shown.
 */
const CHATBOX_TOP_RESERVE = 165;

/** Shortest chat box a resize drag can leave (legacy's floor). */
const CHATBOX_MIN_HEIGHT = 80;

/** Commands that only exist in CTF, hidden from hints and completion elsewhere. */
const CTF_ONLY_COMMANDS: ReadonlySet<string> = new Set(['/switch', '/match', '/elections', '/usurp']);

/** Tag in front of a copied line, matching the badge the line shows. */
const COPY_TAG: Record<ChatMessageType, string> = {
  public: '',
  say: '',
  system: '',
  team: '[TEAM] ',
  'whisper-to': '[TO] ',
  'whisper-from': '[FROM] ',
};

/** A chat line as plain text for the clipboard. Built from the message rather
 *  than the DOM, so links stay whole (the line shows them shortened) and
 *  -emote- tokens survive to paste back into chat. */
function chatLineCopyText(entry: ChatMessageEntry): string {
  if (!entry.player) return entry.text;
  return `${COPY_TAG[entry.type]}${entry.player.name}: ${entry.text}`;
}

/** Chat command descriptions shown as contextual hints while typing. */
const COMMAND_HINTS: ReadonlyMap<string, string> = new Map([
  ['/s', 'Say — speech bubble visible to nearby players'],
  ['/t', 'Team — message visible only to your team'],
  ['/w', 'Whisper — private message to a player'],
  ['/ignore', 'Ignore — hide all messages from a player'],
  ['/unignore', 'Unignore — stop ignoring a player'],
  ['/votemute', 'Votemute — vote to mute a player'],
  ['/spectate', 'Spectate — watch a player (/spectate name), or alone to enter spectator mode'],
  ['/flag', 'Flag — open flag picker, or /flag name to set directly'],
  ['/flags', 'Flags — open flag picker'],
  ['/upgrade', 'Upgrade — spend a point (/upgrade speed, /upgrade defense, /upgrade 1)'],
  ['/upgrades', 'Upgrades — /upgrades drop [n] or /upgrades reset [speed|defense|energy|missile]'],
  ['/respawn', 'Respawn — respawn as a plane (/respawn predator, /respawn 1)'],
  ['/lags', 'Lags — show lag statistics from the server'],
  ['/players', 'Players — show player count (active, spectating, bots)'],
  ['/profile', 'Profile — show your play stats (time, kills, pickups)'],
  ['/spectators', 'Spectators — show how many players are watching you'],
  ['/server', 'Server — server stats (/server upgrades, powerups, limits, performance, network, frames)'],
  ['/welcome', 'Welcome — show server welcome messages'],
  ['/emotes', 'Emotes — open emote picker panel'],
  ['/amping', 'Ping clients — discover who is running what client in this game'],
  ['/help', 'Help — toggle the help panel'],
  ['/drop', 'Drop — drop the flag (CTF) or drop upgrades (/drop 5)'],
  ['/switch', 'Switch — change teams while spectating (CTF)'],
  ['/match', 'Match — show when the current match started (CTF)'],
  ['/elections', 'Elections — start captain elections (CTF)'],
  ['/usurp', 'Usurp — take the captain position (CTF)'],
]);

/** QBot team command hints (CTF only, sent automatically as team chat) */
const QBOT_HINTS: ReadonlyMap<string, string> = new Map([
  ['#cap', 'Capture — escort flag carrier (aliases: #c, #capture, #escort)'],
  ['#recap', 'Recover — recover our flag (aliases: #r, #rec, #recover)'],
  ['#defend', 'Defend — defend the base'],
  ['#auto', 'Auto — auto-pilot mode'],
  ['#assist', 'Assist — assist a teammate (#assist <player> or #assist me)'],
  ['#drop', 'Drop — stop current action (alias: #d)'],
  ['#leader', 'Leader — designate team leader (#leader <player>)'],
  ['#status', 'Status — show QBot status (alias: #s)'],
  ['#dropnow', 'Drop Now — immediately drop the flag (alias: #dd, #dn)'],
  ['#storm', 'Storm — bots storm the enemy base (not recommended)'],
  ['#type', 'Type — set bot plane type (#type predator, #type mohawk, #type random)'],
  ['#help', 'Help — show QBot command list'],
]);

/** QBot alias → canonical for hint lookups */
const QBOT_ALIAS_TO_HINT: ReadonlyMap<string, string> = new Map([
  ['#c', '#cap'],
  ['#capture', '#cap'],
  ['#e', '#cap'],
  ['#esc', '#cap'],
  ['#escort', '#cap'],
  ['#r', '#recap'],
  ['#rec', '#recap'],
  ['#recover', '#recap'],
  ['#a', '#assist'],
  ['#d', '#drop'],
  ['#l', '#leader'],
  ['#s', '#status'],
  ['#dd', '#dropnow'],
  ['#dn', '#dropnow'],
]);

/** All command names sorted by length for prefix matching. */
const COMMAND_NAMES = [...COMMAND_HINTS.keys()].sort((a, b) => a.length - b.length);

/**
 * Emoticon-to-emoji replacement rules (boundary-aware).
 * Each entry is [emoji, pattern]. Patterns use a captured prefix group and
 * a lookahead suffix to avoid matching inside words (e.g. "abc:Ddef", "<30").
 * Ambiguous patterns (:/  :|  :S  B)  ¬_¬) are deliberately excluded.
 * More specific multi-char patterns (crying, wink-tongue) must come before
 * their shorter overlapping forms (sad, tongue).
 */
const LEFT_BOUNDARY = String.raw`(^|[\s([{"'])`;
const RIGHT_BOUNDARY = String.raw`(?=$|[\s)\]}"'.,!?;:])`;
const EMOTICON_RULES: ReadonlyArray<readonly [string, RegExp]> = [
  ['😭', new RegExp(`${LEFT_BOUNDARY}[:=]''-?\\(${RIGHT_BOUNDARY}`, 'g')],
  ['😢', new RegExp(`${LEFT_BOUNDARY}[:=]'-?\\(${RIGHT_BOUNDARY}`, 'g')],
  ['😃', new RegExp(`${LEFT_BOUNDARY}:-?D${RIGHT_BOUNDARY}`, 'g')],
  ['😆', new RegExp(`${LEFT_BOUNDARY}[xX]-?D${RIGHT_BOUNDARY}`, 'g')],
  ['🙂', new RegExp(`${LEFT_BOUNDARY}[:=]-?\\)${RIGHT_BOUNDARY}`, 'g')],
  ['😉', new RegExp(`${LEFT_BOUNDARY};-?\\)${RIGHT_BOUNDARY}`, 'g')],
  ['😜', new RegExp(`${LEFT_BOUNDARY};-?[pP]${RIGHT_BOUNDARY}`, 'g')],
  ['😛', new RegExp(`${LEFT_BOUNDARY}[:=]-?[pP]${RIGHT_BOUNDARY}`, 'g')],
  ['🙁', new RegExp(`${LEFT_BOUNDARY}[:=]-?\\(${RIGHT_BOUNDARY}`, 'g')],
  ['😮', new RegExp(`${LEFT_BOUNDARY}[:=]-?[oO]${RIGHT_BOUNDARY}`, 'g')],
  ['😄', new RegExp(`${LEFT_BOUNDARY}\\^_?\\^${RIGHT_BOUNDARY}`, 'g')],
  ['❤️', new RegExp(`${LEFT_BOUNDARY}<3${RIGHT_BOUNDARY}`, 'g')],
  ['💔', new RegExp(`${LEFT_BOUNDARY}<\\/3${RIGHT_BOUNDARY}`, 'g')],
];

/** Replace ASCII emoticons with emoji in text, returning the new text and cursor offset adjustment.
 * Only replacements ending at or before `cursorPos` affect the delta, so the caret stays correct
 * when emoticons exist after the cursor (e.g. pasted text). While `typing`, an emoticon that ends
 * the text with the caret after it waits: the boundary ending it is the end of the text, which the
 * next key moves, and ":p" became an emoji before "epe:" could follow; the send replaces it. */
function replaceEmoticons(text: string, cursorPos: number, typing = false): { result: string; cursorDelta: number } {
  let cursorDelta = 0;
  let result = text;
  for (const [emoji, pattern] of EMOTICON_RULES) {
    // Track per-pass offset shift so we can map match positions back to the pass's input coordinates
    let passShift = 0;
    const passLength = result.length;
    const caret = cursorPos + cursorDelta;
    result = result.replace(pattern, (match, prefix: string, offset: number) => {
      // offset is in the current (partially replaced) string; map back to the pass's input
      const matchEnd = offset + match.length - passShift;
      if (typing && matchEnd === passLength && caret === passLength) return match;
      const replacement = prefix + emoji;
      const delta = replacement.length - match.length;
      if (matchEnd <= cursorPos + cursorDelta) {
        cursorDelta += delta;
      }
      passShift += delta;
      return replacement;
    });
  }
  return { result, cursorDelta };
}

/**
 * Game Chat - In-game chat panel
 *
 * Displays chat messages, handles input, minimize/maximize, and resize.
 * Replaces the legacy DOM-based chat in UI.ts.
 *
 * @element game-chat
 */
@customElement('game-chat')
export class GameChat extends LitElement {
  static override styles = [
    tokens,
    scrollbarThinHover,
    css`
      :host {
        display: block;
        pointer-events: none;
        z-index: 5;
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
        /* The minimized bubble, shared with the radio stacked above it. */
        --maximize-bottom: 24px;
        --maximize-size: 33px;
      }

      /* ===== Chat wrapper (stacks input + chatbox vertically) ===== */

      .chat-wrapper {
        width: 100%;
        pointer-events: auto;
      }

      /* ===== Chatbox ===== */

      .chatbox-area {
        position: relative;
      }

      .chatbox {
        width: 100%;
        height: 160px;
        /* Wins over the inline resize height, and grows back to it in a
           taller window (chat-area caps the width the same way). */
        max-height: calc(100vh - ${unsafeCSS(CHATBOX_TOP_RESERVE)}px);
        /* Reading position is held by hand (captureReadingAnchor), the same
           way in every browser; Safari has no scroll anchoring to lean on. */
        overflow-anchor: none;
        box-sizing: border-box;
        border-radius: var(--radius-md);
        padding: 32px 8px 8px;
        font-size: var(--font-size-13);
        overflow-y: auto;
        overflow-x: hidden;
        cursor: default;
        transition: 0.25s background-color linear;
      }

      .chatbox .line {
        margin-bottom: 4px;
        text-shadow: var(--shadow-text);
        line-height: 18px;
        cursor: pointer;
      }

      /* "Press Enter to chat" isn't a message, so there's nothing to copy. */
      .chatbox .line.hint {
        cursor: default;
      }

      .chatbox .line .tag {
        display: inline-block;
        text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.5);
        box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.5);
        color: var(--color-text-white);
        border-radius: var(--radius-sm);
        padding: 2px 4px;
        margin-right: 6px;
        font-size: var(--font-size-sm);
        font-weight: bold;
      }

      .chatbox .line .whisper {
        background-color: var(--color-chat-whisper);
      }

      .chatbox .line .team {
        background-color: var(--color-chat-team);
      }

      /* 💬 emoji separating name and text on SAY messages — matches StarMash. */
      .chatbox .line .say-icon {
        margin: 0 2px 0 4px;
      }

      .chatbox .line .player-name {
        cursor: pointer;
        white-space: nowrap;
      }

      .chatbox .line .nick {
        padding-right: 10px;
        color: var(--color-text-white);
        font-weight: bold;
      }

      /* CTF identity colors on public/say nicks — StarMash colors who's
         talking by side. Named team-* because bare .blue is already the
         team-channel purple (vanilla class naming). */
      .chatbox .line .nick.team-blue {
        color: var(--color-team-blue);
      }

      .chatbox .line .nick.team-red {
        color: var(--color-team-red);
      }

      .chatbox .line a {
        color: var(--color-link-url);
      }

      .chatbox .line .inline-emote {
        display: inline-block;
        width: 18px;
        height: 18px;
        vertical-align: middle;
        margin: 0 1px;
      }

      /* Flags drawn as backgrounds: the sender's, inline -flagXX- tokens, and
         the /flag hint's preview (which resizes it below). */
      .flag,
      .chatbox .line .inline-flag {
        display: inline-block;
        width: 20px;
        height: 16px;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        vertical-align: middle;
      }

      .flag {
        margin-right: 5px;
      }

      .chatbox .line .inline-flag {
        margin: 0 2px;
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .chatbox .line .text {
        color: var(--color-text-primary);
      }

      .chatbox .line .green {
        color: var(--color-chat-whisper-text);
      }

      .chatbox .line .blue {
        color: var(--color-chat-team-text);
      }

      .chat-wrapper.hovered .chatbox,
      .chat-wrapper:hover .chatbox {
        background-color: var(--color-bg-selector);
      }

      .chat-wrapper.resizing .chatbox {
        pointer-events: none;
      }

      .chat-wrapper.resizing,
      .chat-wrapper.resizing * {
        user-select: none;
      }

      /* ===== Controls ===== */

      .controls {
        display: none;
        position: absolute;
        top: 0;
        right: 0;
        gap: 2px;
        align-items: center;
        padding: 6px;
        border-radius: 0 var(--radius-md) 0 var(--radius-md);
        background-color: var(--color-bg-chip);
      }

      .chat-wrapper.hovered .controls,
      .chat-wrapper:hover .controls {
        display: flex;
      }

      .control-btn {
        position: relative;
        width: 20px;
        height: 20px;
        cursor: pointer;
        opacity: 0.5;
        pointer-events: auto;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .control-btn:hover {
        opacity: 1;
      }

      /* A pen or finger drag on a touch laptop resizes rather than pans. */
      .control-btn.resize {
        touch-action: none;
      }

      .control-btn .icon {
        width: 14px;
        height: 14px;
        background-color: var(--color-text-white);
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }

      .icon-clear {
        mask-image: url('/vanilla/ui/chat/eraser.svg');
        -webkit-mask-image: url('/vanilla/ui/chat/eraser.svg');
      }

      .icon-minimize {
        mask-image: url('/vanilla/ui/chat/minus.svg');
        -webkit-mask-image: url('/vanilla/ui/chat/minus.svg');
      }

      .icon-resize {
        mask-image: url('/vanilla/ui/chat/move-diagonal.svg');
        -webkit-mask-image: url('/vanilla/ui/chat/move-diagonal.svg');
      }

      /* ===== Tooltips ===== */

      .control-btn .tooltip {
        position: absolute;
        top: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%);
        background-color: var(--color-bg-tooltip);
        color: var(--color-text-tooltip);
        border-radius: var(--radius-sm);
        padding: 4px 8px;
        font-size: var(--font-size-xs);
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        visibility: hidden;
        transition:
          opacity 0.15s ease 0s,
          visibility 0s linear 0.15s;
        z-index: 20;
      }

      .control-btn .tooltip .arrow {
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border: 5px solid transparent;
        border-bottom-color: var(--color-bg-tooltip);
      }

      .control-btn:hover .tooltip {
        opacity: 1;
        visibility: visible;
        transition:
          opacity 0.15s ease 0.4s,
          visibility 0s linear 0.4s;
      }

      /* ===== Chat input ===== */

      .input-wrapper {
        position: relative;
        margin-bottom: 10px;
        height: 30px;
        background-color: var(--color-bg-chip);
        border-radius: var(--radius-xs);
      }

      .chat-input {
        position: relative;
        z-index: 0;
        font-size: var(--font-size-13);
        font-family: var(--font-family);
        width: 100%;
        height: 30px;
        background: transparent;
        color: var(--color-text-link);
        text-shadow: var(--shadow-text);
        border: none;
        outline: none;
        border-radius: var(--radius-xs);
        padding-left: 12px;
        pointer-events: auto;
        box-sizing: border-box;
        -webkit-appearance: none;
        /* The host turns selection off for the chat lines; typed text keeps it. */
        -webkit-user-select: text;
        user-select: text;
      }

      .autocomplete-ghost {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 30px;
        line-height: 30px;
        /* font-size 0 collapses the template's literal newline + indentation
           between the div and its spans to zero width. With white-space: pre
           here instead (the old bug), that source formatting rendered as a
           line break + spaces, pushing both spans onto a second line that
           overflow: hidden then clipped — the ghost was invisible since day
           one. The spans restore their own font-size below. */
        font-size: 0;
        font-family: var(--font-family);
        padding-left: 12px;
        pointer-events: none;
        box-sizing: border-box;
        overflow: hidden;
        z-index: 1;
      }

      /* white-space: pre so the hidden typed-prefix measures exactly like the
         input's text, trailing spaces included. */
      .ghost-typed,
      .ghost-suggestion {
        font-size: var(--font-size-13);
        white-space: pre;
      }

      .ghost-typed {
        visibility: hidden;
      }

      .ghost-suggestion {
        /* Needs to survive over bright map areas behind the half-transparent
           chip — at 0.35 alpha with no shadow it effectively vanished. Same
           text-shadow as the input text it sits beside. */
        color: rgba(255, 255, 255, 0.55);
        text-shadow: var(--shadow-text);
      }

      /* ===== Command hint bar ===== */

      .command-hint {
        font-size: var(--font-size-sm);
        color: rgba(255, 255, 255, 0.6);
        padding: 5px 12px;
        margin-bottom: 2px;
        background-color: rgba(80, 140, 220, 0.12);
        border-radius: var(--radius-sm);
      }

      .command-hint .cmd {
        color: var(--color-text-link);
        font-weight: 600;
      }

      .command-hint .flag {
        width: 16px;
        height: 12px;
        vertical-align: baseline;
        margin-left: 6px;
      }

      /* ===== Maximize button (when minimized) ===== */

      .maximize-btn {
        position: absolute;
        left: 30px;
        bottom: var(--maximize-bottom);
        width: var(--maximize-size);
        height: var(--maximize-size);
        background-repeat: no-repeat;
        background-size: var(--maximize-size) var(--maximize-size);
        background-image: url('data:image/svg+xml;charset=utf-8,%3C?xml version=%221.0%22 encoding=%22UTF-8%22 standalone=%22no%22?%3E %3C!DOCTYPE svg PUBLIC %22-//W3C//DTD SVG 1.1//EN%22 %22http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd%22%3E %3Csvg width=%22100%25%22 height=%22100%25%22 viewBox=%220 0 33 33%22 version=%221.1%22 xmlns=%22http://www.w3.org/2000/svg%22 xmlns:xlink=%22http://www.w3.org/1999/xlink%22 xml:space=%22preserve%22 style=%22fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:1.41421;%22%3E %3Cg transform=%22matrix(0.533333,0,0,0.533333,1,1)%22%3E %3Cpath d=%22M6,2L54,2C57.252,2 60,4.748 60,8L60,41C60,44.252 57.252,47 54,47L25.442,47L15.74,57.673C15.546,57.885 15.276,58 15,58C14.879,58 14.757,57.978 14.639,57.933C14.254,57.784 14,57.413 14,57L14,47L6,47C2.748,47 0,44.252 0,41L0,8C0,4.748 2.748,2 6,2Z%22 style=%22fill-rule:nonzero;%22/%3E %3C/g%3E %3Cg transform=%22matrix(0.533333,0,0,0.533333,0,0)%22%3E %3Cpath d=%22M6,2L54,2C57.252,2 60,4.748 60,8L60,41C60,44.252 57.252,47 54,47L25.442,47L15.74,57.673C15.546,57.885 15.276,58 15,58C14.879,58 14.757,57.978 14.639,57.933C14.254,57.784 14,57.413 14,57L14,47L6,47C2.748,47 0,44.252 0,41L0,8C0,4.748 2.748,2 6,2Z%22 style=%22fill:white;fill-rule:nonzero;%22/%3E %3C/g%3E %3C/svg%3E');
        cursor: pointer;
        opacity: 0.5;
        pointer-events: auto;
      }

      .maximize-btn:hover {
        opacity: 1;
      }

      /* The bubble is positioned against chat-area's bottom edge, so padding
         this tall lifts the radio clear of it; the radio box's own bottom
         margin is the gap. */
      .radio-above-bubble {
        padding-bottom: calc(var(--maximize-bottom) + var(--maximize-size));
      }

      .unread-badge {
        position: absolute;
        font-size: var(--font-size-sm);
        left: 20px;
        top: -7px;
        padding: 3px 6px;
        background-color: #000;
        color: var(--color-text-white);
        text-shadow: var(--shadow-text);
        border-radius: 6px;
      }
    `,
  ];

  private state = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  private wrapperRef: Ref<HTMLElement> = createRef();
  private chatboxRef: Ref<HTMLElement> = createRef();
  private inputRef: Ref<HTMLInputElement> = createRef();

  // Scroll decisions: made in willUpdate before the DOM changes, applied in updated()
  private prevLastEntryKey: number | null = null;
  private scrollToLatest = false;
  /** A line in view while reading back, and where it sat, so updated() can put it back. */
  private readingAnchor: ReadingAnchor | null = null;
  /** Whether the view is at the newest line, kept from the box's own scroll
   *  events: a reflow (the window narrowing or shortening, the box resized)
   *  moves the newest line out of view without one, and the reader did not
   *  scroll away. Measured as a line arrived instead, a narrower window
   *  stopped the chat following new lines until the reader scrolled. */
  private followingLatest = true;

  // Tracks previous input visibility to focus only on open transition
  private wasInputVisible = false;

  /** Text left in the input when a blur closed it (a click on the game,
   *  alt-tab), restored on the next open. Enter and Escape clear it, and an
   *  open with a prefill (Whisper, T, /) replaces it. */
  private draft = '';

  // Chat message history (up/down arrow recall)
  private sentHistory: string[] = [];
  private historyIndex = 0;
  private static readonly MAX_HISTORY = 50;

  // Autocomplete state
  private autocompleteSuggestion = '';
  private autocompleteFullName = '';
  /** True when the suggestion is an inline -name- emote/flag token (replaces
   *  the trailing token in place) rather than a command argument. Tracked
   *  explicitly because shape-sniffing the name breaks on players whose names
   *  themselves look like tokens (e.g. a player called "-O-"). */
  private autocompleteIsEmoteToken = false;
  @state() private ghostTyped = '';
  @state() private ghostSuggestion = '';
  @state() private commandHint = '';
  @state() private hintFlagCode: FlagCode | null = null;

  // Resize drag state (component-local, not reactive)
  private dragState: {
    pointerId: number;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null = null;
  private currentWidth: number | null = null;
  @state() private currentHeight: number | null = null;
  private isResizing = false;

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = 0;
    }
    this.dragState = null;
    this.isResizing = false;
    this.removeResizeListeners();
  }

  private hasLoadedDimensions = false;

  private isScrolledToBottom(container: HTMLElement) {
    return container.scrollHeight - container.scrollTop - container.clientHeight < 24;
  }

  /** Every scroll of the box, the reader's and the code's own: at the bottom
   *  the view follows new lines, anywhere else it holds. */
  private handleChatboxScroll = (): void => {
    const container = this.chatboxRef.value;
    if (container) this.followingLatest = this.isScrolledToBottom(container);
  };

  override willUpdate() {
    // Load saved dimensions once when settings become available (before render, no extra cycle)
    if (!this.hasLoadedDimensions) {
      const { chatWidth, chatHeight } = this.state.displaySettings;
      if (chatWidth !== null || chatHeight !== null) {
        this.hasLoadedDimensions = true;
        if (chatWidth !== null) {
          this.currentWidth = chatWidth;
          requestAnimationFrame(() => this.dispatchResizeWidth(chatWidth));
        }
        if (chatHeight !== null) {
          this.currentHeight = chatHeight;
        }
      }
    }

    // Compute input-derived reactive state before render (avoids extra cycle in updated())
    const chat = this.state.chat;
    if (chat.isInputVisible && !this.wasInputVisible) {
      const opening = chat.inputPrefill ?? this.draft;
      if (opening) {
        this.updateCommandHint(opening);
        this.updateAutocomplete(opening);
      }
    }

    this.decideScroll();
  }

  /**
   * Decides how the chat box should scroll once Lit has changed the DOM. This
   * re-renders on every uiState change, so it only measures when a new message
   * has arrived. At the newest line (or for a line that jumps there) it
   * follows; while reading back it holds a visible line in place.
   */
  private decideScroll(): void {
    this.scrollToLatest = false;
    this.readingAnchor = null;
    const chat = this.state.chat;
    if (chat.isMinimized) return;

    const messages = chat.messages;
    const newest = messages[messages.length - 1];
    const lastEntryKey = newest?.id ?? null;
    const container = this.chatboxRef.value;
    if (!container?.isConnected) {
      // A fresh chat box (first render, or reopened from minimized) opens at
      // the newest line, as legacy's maximizeChat did.
      this.scrollToLatest = true;
      this.followingLatest = true;
      this.prevLastEntryKey = lastEntryKey;
      return;
    }

    if (lastEntryKey === this.prevLastEntryKey) return;
    this.prevLastEntryKey = lastEntryKey;

    if (!newest || this.followingLatest || jumpsToLatest(newest)) {
      this.scrollToLatest = true;
      this.followingLatest = true;
    } else {
      this.readingAnchor = captureReadingAnchor(container);
    }
  }

  override updated() {
    const chat = this.state.chat;
    const container = this.chatboxRef.value;
    const input = this.inputRef.value;

    if (container && this.scrollToLatest) {
      container.scrollTop = container.scrollHeight;
      requestAnimationFrame(() => {
        const c = this.chatboxRef.value;
        if (c) c.scrollTop = c.scrollHeight;
      });
    } else if (container && this.readingAnchor) {
      restoreReadingAnchor(container, this.readingAnchor);
    }

    // Focus and prefill management — only DOM effects on open transition
    if (chat.isInputVisible && input) {
      if (!this.wasInputVisible) {
        const opening = chat.inputPrefill ?? this.draft;
        this.draft = '';
        if (opening) {
          input.value = opening;
        }
        input.focus();
        this.historyIndex = this.sentHistory.length;
        this.wasInputVisible = true;
      }
    } else {
      // Safety net for closes that bypass the explicit handlers (programmatic
      // close, external state change): clear stale hint/ghost on the
      // visible→hidden edge so the next open starts clean.
      if (this.wasInputVisible) {
        this.clearInputUiState();
      }
      this.wasInputVisible = false;
    }
  }

  override render() {
    if (this.state.isMobile) return nothing;

    const chat = this.state.chat;

    if (chat.isMinimized) {
      return this.renderMinimized(chat.unreadCount);
    }

    const chatboxStyle = this.buildChatboxStyle();
    // Read once for the whole list rather than once per line inside the repeat
    const gameType = this.state.gameType;

    return html`
      <div class="chat-wrapper" ${ref(this.wrapperRef)}>
        <radio-panel></radio-panel>
        ${chat.isInputVisible ? this.renderInput() : nothing}
        <div class="chatbox-area">
          <div
            class="chatbox scrollbar-thin-hover"
            ${ref(this.chatboxRef)}
            style=${chatboxStyle}
            @scroll=${this.handleChatboxScroll}
          >
            ${
              !chat.isHintRemoved
                ? html`<div class="line hint"><span class="text">Press Enter to chat</span></div>`
                : nothing
            }
            ${repeat(
              chat.messages,
              (msg) => msg.id,
              // A line never changes once sent, so it's only rebuilt when the
              // game type changes (CTF nick colours). This re-renders on every
              // uiState change and keystroke; unchanged lines cost nothing.
              (msg) => guard([msg, gameType], () => this.renderMessage(msg)),
            )}
          </div>
          <div class="controls">
            <div class="control-btn" @click=${this.handleClear}>
              <div class="icon icon-clear"></div>
              <span class="tooltip"><span class="arrow"></span>Clear${this.keyHint('CLEARCHAT')}</span>
            </div>
            <div class="control-btn" @click=${this.handleMinimize}>
              <div class="icon icon-minimize"></div>
              <span class="tooltip"><span class="arrow"></span>Minimize${this.keyHint('MINIMIZECHAT')}</span>
            </div>
            <div class="control-btn resize" @pointerdown=${this.handleResizeStart}>
              <div class="icon icon-resize"></div>
              <span class="tooltip"><span class="arrow"></span>Resize</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // ===== Render helpers =====

  /** " (KEY)" for a control's tooltip, tracking remaps; empty when unbound. */
  private keyHint(bindName: BindName): string {
    const key = this.state.keybinds.bindByName[bindName];
    return key ? ` (${key})` : '';
  }

  private renderMinimized(unreadCount: number) {
    // Z/X/C still open the radio with chat minimized, and its digits send, so
    // it has to show here too. Only mounted while open, so its padding doesn't
    // move an open emotes panel.
    return html`
      ${this.state.radio ? html`<radio-panel class="radio-above-bubble"></radio-panel>` : nothing}
      <div class="maximize-btn" @click=${this.handleMaximize}>
        ${unreadCount > 0 ? html`<span class="unread-badge">${unreadCount}</span>` : nothing}
      </div>
    `;
  }

  private renderInput() {
    return html`
      ${this.commandHint ? html`<div class="command-hint">${this.renderCommandHint()}</div>` : nothing}
      <div class="input-wrapper">
        <input
          class="chat-input"
          type="text"
          maxlength=${CHAT_INPUT_MAX_CHARS}
          autocomplete="off"
          spellcheck="false"
          ${ref(this.inputRef)}
          @keydown=${this.handleInputKeydown}
          @input=${this.handleChatInput}
          @blur=${this.handleInputBlur}
        />
        <div class="autocomplete-ghost">
          <span class="ghost-typed">${this.ghostTyped}</span
          ><span class="ghost-suggestion">${this.ghostSuggestion}</span>
        </div>
      </div>
    `;
  }

  private renderCommandHint() {
    const flag = this.hintFlagCode;
    const spaceIdx = this.commandHint.indexOf(' — ');
    if (spaceIdx === -1) {
      return flag ? html`${this.commandHint} <span class="flag" style=${flagStyle(flag)}></span>` : this.commandHint;
    }
    const cmd = this.commandHint.slice(0, spaceIdx);
    const desc = this.commandHint.slice(spaceIdx);
    return flag
      ? html`<span class="cmd">${cmd}</span>${desc} <span class="flag" style=${flagStyle(flag)}></span>`
      : html`<span class="cmd">${cmd}</span>${desc}`;
  }

  private renderMessage(entry: ChatMessageEntry) {
    switch (entry.type) {
      case 'public':
        return this.renderPlayerMessage(entry);
      case 'whisper-to':
        return this.renderWhisperMessage(entry, 'TO');
      case 'whisper-from':
        return this.renderWhisperMessage(entry, 'FROM');
      case 'team':
        return this.renderTeamMessage(entry);
      case 'say':
        return this.renderSayMessage(entry);
      case 'system':
        return this.renderSystemMessage(entry);
    }
  }

  private renderPlayerMessage(entry: ChatMessageEntry) {
    const player = entry.player;
    if (!player) return nothing;

    return html`
      <div class="line" data-msg-id=${entry.id} @click=${this.handleLineCopy}>
        <span
          class="player-name"
          data-player-id=${player.id}
          @click=${this.handlePlayerClick}
          @contextmenu=${this.handlePlayerClick}
        >
          <span class="flag" style=${flagStyle(player.flag)} title=${player.flagLabel}></span>
          <span class="nick${this.nickTeamClass(player)}">${player.name}</span>
        </span>
        <span class="text">${renderChatText(entry.text)}</span>
      </div>
    `;
  }

  /** CTF-only identity class suffix for public/say nicks (StarMash behaviour):
   *  blue or red by the sender's side. FFA/BTR "teams" are per-player
   *  join-order values, so never colorize outside a real team mode. Whispers
   *  keep green and TEAM lines keep the channel purple — on a team line the
   *  sender is always your side, so identity adds nothing. */
  private nickTeamClass(player: ChatMessagePlayer): string {
    if (this.state.gameType === 'ctf') {
      if (player.team === 1) return ' team-blue';
      if (player.team === 2) return ' team-red';
    }
    return '';
  }

  private renderWhisperMessage(entry: ChatMessageEntry, direction: string) {
    const player = entry.player;
    if (!player) return nothing;

    return html`
      <div class="line" data-msg-id=${entry.id} @click=${this.handleLineCopy}>
        <span class="tag whisper">${direction}</span>
        <span
          class="player-name"
          data-player-id=${player.id}
          @click=${this.handlePlayerClick}
          @contextmenu=${this.handlePlayerClick}
        >
          <span class="nick green">${player.name}</span>
        </span>
        <span class="text green">${renderChatText(entry.text)}</span>
      </div>
    `;
  }

  private renderTeamMessage(entry: ChatMessageEntry) {
    const player = entry.player;
    if (!player) return nothing;

    return html`
      <div class="line" data-msg-id=${entry.id} @click=${this.handleLineCopy}>
        <span class="tag team">TEAM</span>
        <span
          class="player-name"
          data-player-id=${player.id}
          @click=${this.handlePlayerClick}
          @contextmenu=${this.handlePlayerClick}
        >
          <span class="nick blue">${player.name}</span>
        </span>
        <span class="text blue">${renderChatText(entry.text)}</span>
      </div>
    `;
  }

  /** SAY ("/s") proximity chat — flag + name + 💬 speech-bubble + text. Matches
   *  StarMash's wire format so messages from /s look the same in both clients. */
  private renderSayMessage(entry: ChatMessageEntry) {
    const player = entry.player;
    if (!player) return nothing;

    return html`
      <div class="line" data-msg-id=${entry.id} @click=${this.handleLineCopy}>
        <span
          class="player-name"
          data-player-id=${player.id}
          @click=${this.handlePlayerClick}
          @contextmenu=${this.handlePlayerClick}
        >
          <span class="flag" style=${flagStyle(player.flag)} title=${player.flagLabel}></span>
          <span class="nick${this.nickTeamClass(player)}">${player.name}</span>
        </span>
        <span class="say-icon">💬</span>
        <span class="text">${renderChatText(entry.text)}</span>
      </div>
    `;
  }

  private renderSystemMessage(entry: ChatMessageEntry) {
    return html`
      <div class="line" data-msg-id=${entry.id} @click=${this.handleLineCopy}>
        ${entry.isWarning ? html`<span class="nick">⚠</span>` : nothing}
        <span class="text">${renderChatText(entry.text)}</span>
      </div>
    `;
  }

  /** Dispatch width to parent chat-area container */
  private dispatchResizeWidth(width: number) {
    this.dispatchEvent(new CustomEvent('chat-resize-width', { detail: { width }, bubbles: true, composed: true }));
  }

  /** Build inline style for chatbox height (for resize drag) */
  private buildChatboxStyle(): string {
    // While dragging, render the live drag height so a mid-resize re-render
    // (new message, hint update) doesn't snap the chatbox back to the last
    // committed height — handleResizeMove only writes the DOM, not currentHeight.
    if (this.isResizing) {
      return `height: ${this.pendingResizeH}px`;
    }
    if (this.currentHeight !== null) {
      return `height: ${this.currentHeight}px`;
    }
    return '';
  }

  // ===== Event handlers =====

  private handleClear = (e: MouseEvent) => {
    e.stopPropagation();
    this.api.playClick();
    this.api.clearChat();
  };

  private handleMinimize = (e: MouseEvent) => {
    e.stopPropagation();
    this.api.playClick();
    this.api.minimizeChat();
  };

  private handleMaximize = () => {
    this.api.playClick();
    this.api.maximizeChat();
  };

  private handleInputKeydown = (e: KeyboardEvent) => {
    // Keys the IME is still processing must not act as chat controls: the
    // Enter that picks an emoji in the Windows emoji panel (or commits a CJK
    // composition) would otherwise send + close the input mid-composition,
    // and arrows navigating the candidate list would walk sent-history.
    // Chromium tags these keydowns with isComposing; Safari fires the
    // composition-commit keydown after compositionend with only the legacy
    // keyCode 229. Stop propagation so the window-level game handler doesn't
    // treat them as chat-passthrough keys (Enter → toggleChatBox) either.
    if (e.isComposing || e.keyCode === 229) {
      e.stopPropagation();
      return;
    }
    const input = this.inputRef.value;
    if (e.key === 'Enter') {
      e.stopPropagation();
      e.preventDefault();
      // The press that opened the box went to the window, and its auto-repeat lands here once the box has
      // focus; a repeat must neither send the draft nor close the box
      if (e.repeat) return;
      if (input) {
        const message = this.withEndEmoticonReplaced(input.value).trim();
        if (message) {
          this.api.submitChatMessage(message);
          this.sentHistory.push(message);
          if (this.sentHistory.length > GameChat.MAX_HISTORY) {
            this.sentHistory.shift();
          }
        }
        input.value = '';
        this.historyIndex = this.sentHistory.length;
      }
      this.draft = '';
      this.clearInputUiState();
      this.api.closeChatInput();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      e.preventDefault();
      // Escape is the deliberate discard, unlike a blur
      this.draft = '';
      this.clearInputUiState();
      this.api.closeChatInput();
    } else if (e.key === 'Tab') {
      e.stopPropagation();
      e.preventDefault();
      if (input) {
        if (this.autocompleteSuggestion && this.autocompleteFullName) {
          // Inline -name- token at end of input: replace the partial token in
          // place. Branch on how the suggestion was created — NOT on the name
          // shape, which misroutes players named like tokens (e.g. "-O-").
          if (this.autocompleteIsEmoteToken) {
            const tokenMatch = input.value.match(/(?:^|\s)-[a-z0-9-]+$/i);
            if (tokenMatch) {
              const tokenStart = input.value.length - tokenMatch[0].length + (tokenMatch[0].startsWith('-') ? 0 : 1);
              input.value = input.value.slice(0, tokenStart) + this.autocompleteFullName + ' ';
            }
          } else {
            // Player name autocomplete: replace typed prefix with full name + space
            const spaceIndex = input.value.indexOf(' ');
            const cmd = input.value.slice(0, spaceIndex + 1);
            input.value = cmd + this.autocompleteFullName + ' ';
          }
          this.clearAutocomplete();
          this.updateCommandHint(input.value);
          // No-op today (the completed value ends in a space), kept symmetric
          // with the command-completion branch and safe if a future completion
          // transitions straight into a new autocomplete context.
          this.updateAutocomplete(input.value);
        } else {
          // Command autocomplete: complete partial command name
          const completed = this.completeCommand(input.value);
          if (completed) {
            input.value = completed;
            this.updateCommandHint(input.value);
            this.updateAutocomplete(input.value);
          }
        }
      }
    } else if (e.key === 'ArrowUp') {
      e.stopPropagation();
      if (input && this.historyIndex > 0) {
        e.preventDefault();
        this.historyIndex--;
        input.value = this.sentHistory[this.historyIndex] ?? '';
        this.updateCommandHint(input.value);
        this.updateAutocomplete(input.value);
      }
    } else if (e.key === 'ArrowDown') {
      e.stopPropagation();
      if (input && this.historyIndex < this.sentHistory.length) {
        e.preventDefault();
        this.historyIndex++;
        input.value = this.historyIndex < this.sentHistory.length ? (this.sentHistory[this.historyIndex] ?? '') : '';
        this.updateCommandHint(input.value);
        this.updateAutocomplete(input.value);
      }
    }
  };

  /** Commands that take a player name as the next argument */
  private static readonly NAME_COMMANDS = ['/w', '/ignore', '/unignore', '/votemute', '/spectate'];

  /** Whether the command's next argument is a player name: the chat commands
   *  above, and the team macros #assist and #leader. */
  private static takesNameArgument(cmd: string): boolean {
    return GameChat.NAME_COMMANDS.includes(cmd) || cmd === '#assist' || cmd === '#leader';
  }

  /** True while the input is a name-taking command with its first argument
   *  still being typed (no space after the name yet). */
  private isTypingNameArgument(value: string): boolean {
    const firstSpace = value.indexOf(' ');
    if (firstSpace === -1) return false;
    if (value.indexOf(' ', firstSpace + 1) !== -1) return false;
    return GameChat.takesNameArgument(value.slice(0, firstSpace).toLowerCase());
  }

  /** /server subcommand names for autocomplete */
  private static readonly SERVER_SUBCOMMANDS = [
    'sync',
    'debug',
    'frames',
    'limits',
    'network',
    'upgrades',
    'powerups',
    'performance',
  ];

  /** Plane names for /respawn autocomplete (abbreviations first for preferred match order) */
  private static readonly PLANE_NAMES = [
    'pred',
    'goli',
    'heli',
    'prow',
    'torn',
    'mohawk',
    'goliath',
    'prowler',
    'tornado',
    'predator',
  ];

  /** Upgrade names for /upgrade autocomplete (abbreviations first for preferred match order) */
  private static readonly UPGRADE_NAMES = ['def', 'speed', 'energy', 'defense', 'missile'];

  private static findFromList(list: readonly string[], prefix: string): { name: string; isPrefix: boolean } | null {
    const lower = prefix.toLowerCase();
    for (const name of list) {
      if (name.startsWith(lower) && name !== lower) {
        return { name, isPrefix: true };
      }
    }
    return null;
  }

  private handleChatInput = () => {
    const input = this.inputRef.value;
    if (!input) return;

    // Replace emoticons FIRST so the hint bar and autocomplete derive from the
    // final text — otherwise an automatic emoji substitution leaves them one
    // keystroke behind the actual input value.
    this.applyEmoticonReplacement(input);

    const value = input.value;
    this.updateCommandHint(value);
    this.updateAutocomplete(value);
  };

  /** Where the input's message body starts, the part emoticons are replaced
   *  in: after the name for a whisper (escaped, spaces as &s;, when prefilled
   *  from the context menu, so one token), after the command for a say, the
   *  whole text otherwise. Null while there is no body yet, or when the
   *  argument is a player name (/ignore xD, /spectate, #assist), left as typed. */
  private emoticonBodyStart(value: string): number | null {
    const firstSpace = value.indexOf(' ');
    const cmd = (firstSpace === -1 ? value : value.slice(0, firstSpace)).toLowerCase();
    if (cmd === '/s') return firstSpace === -1 ? null : firstSpace + 1;
    if (cmd === '/w') {
      const spaceAfterName = firstSpace === -1 ? -1 : value.indexOf(' ', firstSpace + 1);
      return spaceAfterName === -1 ? null : spaceAfterName + 1;
    }
    return GameChat.takesNameArgument(cmd) ? null : 0;
  }

  /** Replace ASCII emoticons with emoji in the input's message body, fixing up
   *  the caret. No-op until the relevant prefix has a body to replace in. */
  private applyEmoticonReplacement(input: HTMLInputElement): void {
    const value = input.value;
    const textStart = this.emoticonBodyStart(value);
    if (textStart === null) return;
    const cursor = input.selectionStart ?? 0;

    const before = value.slice(0, textStart);
    const text = value.slice(textStart);
    const { result, cursorDelta } = replaceEmoticons(text, cursor - textStart, true);

    if (result !== text) {
      input.value = before + result;
      const newCursor = cursor <= textStart ? cursor : cursor + cursorDelta;
      input.setSelectionRange(newCursor, newCursor);
    }
  }

  /** The message as sent: an emoticon left at the end while typing is replaced now. */
  private withEndEmoticonReplaced(value: string): string {
    const textStart = this.emoticonBodyStart(value);
    if (textStart === null) return value;
    const text = value.slice(textStart);
    return value.slice(0, textStart) + replaceEmoticons(text, text.length).result;
  }

  /** Whether a command applies right now: /spectate only while spectating, /t
   *  not in FFA, CTF commands only in CTF. Shared by the hint bar and Tab. */
  private isCommandAvailable(name: string): boolean {
    if (name === '/spectate') return this.state.spectator !== null;
    if (name === '/t') return this.state.gameType !== 'ffa';
    if (CTF_ONLY_COMMANDS.has(name)) return this.state.gameType === 'ctf';
    return true;
  }

  /** Complete a partial command name. Returns the full command + space, or null if no match. */
  private completeCommand(value: string): string | null {
    if (!value.startsWith('/') || value.includes(' ')) return null;
    const typed = value.toLowerCase();
    // An already-complete, context-valid command must not Tab-complete into a
    // longer one (e.g. /s → /server, /w → /welcome).
    if (COMMAND_HINTS.has(typed) && this.isCommandAvailable(typed)) return null;
    for (const name of COMMAND_NAMES) {
      if (!this.isCommandAvailable(name)) continue;
      if (name.startsWith(typed) && name !== typed) {
        return name + ' ';
      }
    }
    return null;
  }

  private updateCommandHint(value: string): void {
    this.hintFlagCode = null;

    if (!this.state.gameSettings.showCommandHints) {
      this.commandHint = '';
      return;
    }

    const isSpectating = this.state.spectator !== null;
    const isCTF = this.state.gameType === 'ctf';
    const isFFA = this.state.gameType === 'ffa';

    // QBot # commands (CTF only)
    if (value.startsWith('#') && isCTF) {
      const cmd = value.split(' ')[0]!.toLowerCase();
      if (cmd === '#') {
        this.commandHint = '#cap  #recap  #defend  #assist  #auto  #drop  #leader  #status  #storm  #type';
        return;
      }
      const canonical = QBOT_ALIAS_TO_HINT.get(cmd) ?? cmd;
      const hint = QBOT_HINTS.get(canonical);
      if (hint) {
        this.commandHint = canonical + ' — ' + hint;
        return;
      }
      // Prefix match against QBot commands
      for (const [name, desc] of QBOT_HINTS) {
        if (name.startsWith(cmd)) {
          this.commandHint = name + ' — ' + desc;
          return;
        }
      }
      this.commandHint = '';
      return;
    }

    if (!value.startsWith('/')) {
      this.commandHint = '';
      return;
    }

    // Just "/" alone — show available commands summary
    if (value === '/') {
      const cmds = ['/s', '/w', '/flag', '/ignore', '/votemute'];
      if (!isFFA) cmds.push('/t');
      cmds.push('/drop');
      if (isCTF) cmds.push('/elections');
      if (isSpectating) cmds.push('/spectate');
      cmds.push('/help');
      this.commandHint = cmds.join('  ');
      return;
    }

    // Extract the command token (everything up to first space, or the whole value)
    const spaceIndex = value.indexOf(' ');
    const typed = (spaceIndex === -1 ? value : value.slice(0, spaceIndex)).toLowerCase();

    // Hide context-dependent commands when not applicable
    if (!this.isCommandAvailable(typed)) {
      this.commandHint = '';
      return;
    }

    // Exact match — show hint for known command
    const exact = COMMAND_HINTS.get(typed);
    if (exact) {
      this.commandHint = typed + ' — ' + exact;

      // For /flag with an argument, resolve and show the flag preview
      if ((typed === '/flag' || typed === '/flags') && spaceIndex !== -1) {
        const flagArg = value.slice(spaceIndex + 1).trim();
        if (flagArg.length > 0) {
          // Try exact resolution first, then fall back to prefix match
          const resolved = resolveFlag(flagArg);
          if (resolved) {
            this.hintFlagCode = resolved;
            this.commandHint = typed + ' — ' + resolved;
          } else {
            const prefixMatch = findFlagByPrefix(flagArg);
            if (prefixMatch) {
              const matchResolved = resolveFlag(prefixMatch.name);
              if (matchResolved) this.hintFlagCode = matchResolved;
              this.commandHint = typed + ' — ' + prefixMatch.name;
            } else {
              this.commandHint = typed + ' — no match for "' + flagArg + '"';
            }
          }
        }
      }
      return;
    }

    // Prefix match — show first matching command while still typing
    if (spaceIndex === -1) {
      for (const name of COMMAND_NAMES) {
        if (!this.isCommandAvailable(name)) continue;
        if (name.startsWith(typed)) {
          const hint = COMMAND_HINTS.get(name)!;
          this.commandHint = name + ' — ' + hint;
          return;
        }
      }
    }

    this.commandHint = '';
  }

  /** Bot plane types for #type autocomplete */
  private static readonly BOT_TYPES = ['predator', 'mohawk', 'random'];

  /**
   * Detects a partial `-name` token at the end of input (cursor after last char)
   * and sets a ghost suggestion completing it to `-name-`. Matches emotes and flags.
   * Returns true if a suggestion was set (autocomplete handled), false otherwise.
   */
  private tryInlineEmoteAutocomplete(value: string): boolean {
    // Find a trailing `-word` pattern where the `-` is at start or after whitespace.
    // Allows hyphens inside the word (e.g. `-flaggb-sct`) since flag codes contain them.
    const match = value.match(/(?:^|\s)-([a-z0-9-]+)$/i);
    if (!match) return false;

    const typed = match[1]!.toLowerCase();

    // Flag token takes priority: `-flag{code}` → `-flag{resolved}-`
    if (typed.startsWith('flag') && typed.length > 4) {
      const code = typed.slice(4);
      const flagMatch = findFlagByPrefix(code);
      if (flagMatch && flagMatch.name !== code) {
        this.setAutocomplete(`-flag${flagMatch.name}-`, true, `-flag${code}`, value, true);
        return true;
      }
      // Partial "flag" but no match yet — let it fall through to emote search
    }

    // Emote prefix match (shortest wins)
    let best: string | null = null;
    for (const name of EMOTE_NAMES) {
      if (name === typed) {
        // Already a full emote match — nothing to suggest
        this.clearAutocomplete();
        return false;
      }
      if (name.startsWith(typed) && (!best || name.length < best.length)) {
        best = name;
      }
    }

    if (best) {
      this.setAutocomplete(`-${best}-`, true, `-${typed}`, value, true);
      return true;
    }

    this.clearAutocomplete();
    return false;
  }

  private updateAutocomplete(value: string): void {
    // Inline -name- emote autocomplete works anywhere in chat (regular text
    // or /s) — EXCEPT while typing the first argument of a name-taking
    // command, where a leading dash is part of a player name (e.g. "-O-")
    // and must reach the player-name matcher below instead.
    if (!this.isTypingNameArgument(value) && this.tryInlineEmoteAutocomplete(value)) return;

    const spaceIndex = value.indexOf(' ');
    if (spaceIndex === -1 || (!value.startsWith('/') && !value.startsWith('#'))) {
      this.clearAutocomplete();
      return;
    }

    const cmd = value.slice(0, spaceIndex).toLowerCase();
    const argPrefix = value.slice(spaceIndex + 1);

    // #type autocomplete — match against bot plane types
    if (cmd === '#type') {
      const match = argPrefix.length > 0 ? GameChat.findFromList(GameChat.BOT_TYPES, argPrefix) : null;
      if (match && match.name !== argPrefix.toLowerCase()) {
        this.setAutocomplete(match.name, match.isPrefix, argPrefix, value);
      } else {
        this.clearAutocomplete();
      }
      return;
    }

    // #assist / #leader autocomplete — match against player names
    if (cmd === '#assist' || cmd === '#leader') {
      const match = this.findPlayerByPrefix(argPrefix);
      if (match && match.name.toLocaleLowerCase() !== argPrefix.toLocaleLowerCase()) {
        this.setAutocomplete(match.name, match.isPrefix, argPrefix, value);
      } else {
        this.clearAutocomplete();
      }
      return;
    }

    // Only / commands below this point
    if (!value.startsWith('/')) {
      this.clearAutocomplete();
      return;
    }

    // /respawn autocomplete — match against plane names
    if (cmd === '/respawn') {
      const match = argPrefix.length > 0 ? GameChat.findFromList(GameChat.PLANE_NAMES, argPrefix) : null;
      if (match && match.name !== argPrefix.toLowerCase()) {
        this.setAutocomplete(match.name, match.isPrefix, argPrefix, value);
      } else {
        this.clearAutocomplete();
      }
      return;
    }

    // /upgrade autocomplete — match against upgrade names
    if (cmd === '/upgrade') {
      const match = argPrefix.length > 0 ? GameChat.findFromList(GameChat.UPGRADE_NAMES, argPrefix) : null;
      if (match && match.name !== argPrefix.toLowerCase()) {
        this.setAutocomplete(match.name, match.isPrefix, argPrefix, value);
      } else {
        this.clearAutocomplete();
      }
      return;
    }

    // /server autocomplete — match against subcommands
    if (cmd === '/server') {
      const match = argPrefix.length > 0 ? GameChat.findFromList(GameChat.SERVER_SUBCOMMANDS, argPrefix) : null;
      if (match && match.name !== argPrefix.toLowerCase()) {
        this.setAutocomplete(match.name, match.isPrefix, argPrefix, value);
      } else {
        this.clearAutocomplete();
      }
      return;
    }

    // /flag autocomplete — match against flag names
    if (cmd === '/flag' || cmd === '/flags') {
      const match = argPrefix.length > 0 ? findFlagByPrefix(argPrefix) : null;
      if (match && match.name !== argPrefix.toLowerCase()) {
        this.setAutocomplete(match.name, match.isPrefix, argPrefix, value);
      } else {
        this.clearAutocomplete();
      }
      return;
    }

    // Player name autocomplete for /w, /ignore, etc.
    if (!GameChat.NAME_COMMANDS.includes(cmd)) {
      this.clearAutocomplete();
      return;
    }

    // /spectate only available while spectating
    if (cmd === '/spectate' && this.state.spectator === null) {
      this.clearAutocomplete();
      return;
    }

    // /w reads its target as one word (Chat.ts takes words[1]), so it completes
    // the escaped form the Whisper menu item prefills (spaces as &s;), and stops
    // once a space shows the message has begun. The other name commands read
    // the rest of the line, so they complete plain names, spaces and all.
    const isWhisper = cmd === '/w';
    if (isWhisper && argPrefix.includes(' ')) {
      this.clearAutocomplete();
      return;
    }

    // /unignore only shows ignored players, /ignore excludes them
    const filter = cmd === '/unignore' ? 'ignored-only' : cmd === '/ignore' ? 'not-ignored' : 'all';
    const match = this.findPlayerByPrefix(argPrefix, filter, isWhisper ? escapePlayerName : undefined);
    if (match && match.name.toLocaleLowerCase() !== argPrefix.toLocaleLowerCase()) {
      this.setAutocomplete(match.name, match.isPrefix, argPrefix, value);
    } else {
      this.clearAutocomplete();
    }
  }

  private setAutocomplete(
    fullName: string,
    isPrefix: boolean,
    typedArg: string,
    fullValue: string,
    isEmoteToken = false,
  ): void {
    this.autocompleteIsEmoteToken = isEmoteToken;
    this.autocompleteFullName = fullName;
    if (isPrefix) {
      const remainder = fullName.slice(typedArg.length);
      this.autocompleteSuggestion = remainder;
      this.ghostTyped = fullValue;
      this.ghostSuggestion = remainder;
    } else {
      this.autocompleteSuggestion = fullName;
      this.ghostTyped = fullValue;
      this.ghostSuggestion = ' \u2192 ' + fullName;
    }
  }

  /** Search UIState players for best name match. Prefers prefix, falls back to contains. Excludes bots. Empty prefix returns shortest name.
   *  `nameForm` rewrites each name before matching and in the result (/w's escaped form). */
  private findPlayerByPrefix(
    prefix: string,
    filter: 'all' | 'ignored-only' | 'not-ignored' = 'all',
    nameForm: (name: string) => string = (name) => name,
  ): { id: number; name: string; isPrefix: boolean } | null {
    const lower = prefix.toLocaleLowerCase();
    const ignored = this.state.ignoredPlayerIds;
    let bestPrefix: { id: number; name: string } | null = null;
    let bestContains: { id: number; name: string } | null = null;
    for (const player of this.state.players) {
      if (player.bot) continue;
      if (filter === 'ignored-only' && !ignored.includes(player.id)) continue;
      if (filter === 'not-ignored' && ignored.includes(player.id)) continue;
      const name = nameForm(player.name);
      const nameLower = name.toLocaleLowerCase();
      if (nameLower.startsWith(lower)) {
        if (!bestPrefix || name.length < bestPrefix.name.length) {
          bestPrefix = { id: player.id, name };
        }
      } else if (nameLower.includes(lower)) {
        if (!bestContains || name.length < bestContains.name.length) {
          bestContains = { id: player.id, name };
        }
      }
    }
    if (bestPrefix) return { ...bestPrefix, isPrefix: true };
    if (bestContains) return { ...bestContains, isPrefix: false };
    return null;
  }

  private clearAutocomplete(): void {
    this.autocompleteSuggestion = '';
    this.autocompleteFullName = '';
    this.autocompleteIsEmoteToken = false;
    this.ghostTyped = '';
    this.ghostSuggestion = '';
  }

  /** Reset the transient input chrome (command hint, flag preview, autocomplete
   *  ghost) on every close path. Without this, stale hint/ghost state survives
   *  into the next open — willUpdate only recomputes it when inputPrefill is
   *  non-null, so a plain Enter-to-open would render the previous hint. */
  private clearInputUiState(): void {
    this.clearAutocomplete();
    this.commandHint = '';
    this.hintFlagCode = null;
  }

  /** A blur (a click on the game, alt-tab) has to close the input, or the game
   *  would keep treating keys as typing. What was typed is kept as the draft. */
  private handleInputBlur = () => {
    if (this.state.chat.isInputVisible) {
      this.draft = this.inputRef.value?.value ?? '';
      this.clearInputUiState();
      this.api.closeChatInput();
    }
  };

  /** Opens the player context menu. Reads the player id from the element's
   *  data attribute so the handler stays identity-stable across renders —
   *  otherwise every keystroke (ghost/hint state is reactive) would re-bind a
   *  fresh listener on every visible message line. Mirrors game-log. */
  private handlePlayerClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (e.type === 'contextmenu') e.preventDefault();
    const el = e.currentTarget;
    if (!(el instanceof HTMLElement)) return;
    const playerId = Number(el.dataset['playerId']);
    if (!Number.isFinite(playerId)) return;
    const rect = el.getBoundingClientRect();
    this.api.playClick();
    this.api.showContextMenu(playerId, rect.bottom, rect.left);
  };

  /** Copies the clicked line, looked up by its data-msg-id (a click on a link
   *  follows the link instead). */
  private handleLineCopy = (e: MouseEvent) => {
    if (e.target instanceof Element && e.target.closest('a')) return;
    const line = e.currentTarget;
    if (!(line instanceof HTMLElement)) return;
    const id = Number(line.dataset['msgId']);
    const entry = this.state.chat.messages.find((msg) => msg.id === id);
    if (!entry) return;
    this.api.playClick();
    this.api.copyChatLine(chatLineCopyText(entry));
  };

  // ===== Resize drag handlers =====

  /** Pointer events, so a pen or finger resizes too on a touch laptop. The
   *  move/end listeners sit on window rather than a pointer capture on the
   *  handle: the drag has to end cleanly even if the handle unmounts mid-drag
   *  (the minimize key), and a removed capture target is never told. */
  private handleResizeStart = (e: PointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    if (this.isResizing) return;
    const chatbox = this.chatboxRef.value;
    const wrapper = this.wrapperRef.value;
    if (!chatbox || !wrapper) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const chatboxRect = chatbox.getBoundingClientRect();

    this.dragState = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: wrapperRect.width,
      startHeight: chatboxRect.height,
    };
    this.pendingResizeW = wrapperRect.width;
    this.pendingResizeH = chatboxRect.height;

    this.isResizing = true;
    wrapper.classList.add('hovered', 'resizing');
    window.addEventListener('pointermove', this.handleResizeMove);
    window.addEventListener('pointerup', this.handleResizeEnd);
    window.addEventListener('pointercancel', this.handleResizeEnd);
    window.addEventListener('blur', this.handleResizeEnd);
  };

  private resizeRaf = 0;
  private pendingResizeW = 0;
  private pendingResizeH = 0;

  private handleResizeMove = (e: PointerEvent) => {
    if (!this.dragState || e.pointerId !== this.dragState.pointerId) return;

    let clientY = e.clientY;
    if (window.innerHeight - clientY < 100) {
      clientY = window.innerHeight - 100;
    }

    const maxHeight = window.innerHeight - CHATBOX_TOP_RESERVE;
    const dragged = this.dragState.startHeight - (clientY - this.dragState.startY);
    this.pendingResizeW = Math.min(800, Math.max(330, this.dragState.startWidth + (e.clientX - this.dragState.startX)));
    this.pendingResizeH = Math.max(CHATBOX_MIN_HEIGHT, Math.min(maxHeight, dragged));

    if (!this.resizeRaf) {
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;
        const wrapper = this.wrapperRef.value;
        const chatbox = this.chatboxRef.value;
        if (!wrapper || !chatbox) return;
        this.dispatchResizeWidth(this.pendingResizeW);
        this.applyResizeHeight(chatbox);
      });
    }
  };

  /** Writes the drag height, keeping the newest line in view while following
   *  (a shorter box would otherwise hide the newest lines). */
  private applyResizeHeight(chatbox: HTMLElement): void {
    chatbox.style.height = `${this.pendingResizeH}px`;
    if (this.followingLatest) chatbox.scrollTop = chatbox.scrollHeight;
  }

  private handleResizeEnd = (e: Event) => {
    if (!this.dragState) return;
    // Another finger or pen lifting doesn't end this drag
    if (e instanceof PointerEvent && e.pointerId !== this.dragState.pointerId) return;
    this.dragState = null;
    this.isResizing = false;

    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = 0;
    }

    const wrapper = this.wrapperRef.value;
    const chatbox = this.chatboxRef.value;

    // Flush the last pending resize values so style reads are accurate
    if (chatbox) {
      this.applyResizeHeight(chatbox);
    }
    this.dispatchResizeWidth(this.pendingResizeW);

    if (wrapper) {
      wrapper.classList.remove('hovered', 'resizing');
      this.currentWidth = this.pendingResizeW;
    }
    if (chatbox) {
      const h = parseFloat(chatbox.style.height);
      this.currentHeight = Number.isFinite(h) ? h : null;
    }

    this.api.saveChatDimensions(this.currentWidth, this.currentHeight);
    this.removeResizeListeners();
  };

  private removeResizeListeners(): void {
    window.removeEventListener('pointermove', this.handleResizeMove);
    window.removeEventListener('pointerup', this.handleResizeEnd);
    window.removeEventListener('pointercancel', this.handleResizeEnd);
    window.removeEventListener('blur', this.handleResizeEnd);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'game-chat': GameChat;
  }
  interface HTMLElementEventMap {
    /** game-chat's resize drag, asking chat-area (which owns the width) to follow. */
    'chat-resize-width': CustomEvent<{ width: number }>;
  }
}
